import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseElement } from "../BaseElement";
import { LightTheme } from "../ThemeLight";
import { watch } from "../internal/watch";

import { styles } from "./UserProfilePosts.style";
import { IUserInfo } from "../../backend/cloud/UserTable";

import { IPlaceMediaTable } from "../../backend/IPlaceMediaTable";
import { IMedia, IPlaceMediaItem } from "../../../main";
import { orderBy } from "lodash-es";
import {
    GEO_MEDIA_TYPES,
    getMediaTypeFromMimeType,
    MEDIA_GALLERY_SURVEY_TYPES,
    MEDIA_GALLERY_VIEW_TYPES,
} from "../../Media";
import { LocalDataStore } from "../../DataStore/LocalDataStore";
import { ChangeSet, ChangeSetListener } from "../../DataStore/ChangeSet";
import { DataProvider } from "../../DataStore/DataProvider";

import "../UserProfileAbout/UserProfileAbout";
import "../UserProfileTimeline/UserProfileTimeline";

export async function getUserMedia(
    dataStore: LocalDataStore,
    id: string
): Promise<IPlaceMediaItem[]> {
    const table = dataStore.getSource<IPlaceMediaTable>("place-media");
    const media = Array.from(await table.allOwnedBy(id));
    return orderBy(
        media.filter((x) => x.createdAt),
        (x) => x.media.dateAdded ?? x.createdAt,
        "desc"
    );
}

@customElement("ds-user-profile-posts")
export class UserProfilePosts extends BaseElement implements ChangeSetListener {
    static override styles = [styles, LightTheme];

    @property({ type: Object }) userInfo?: IUserInfo;

    @state() protected _userPlaceMediaItems?: IPlaceMediaItem[];

    private _userId?: string;

    @watch("userInfo")
    async onUserInfoUpdate() {
        if (this.userInfo && this.userInfo.owner) {
            this._userId = this.userInfo.owner;
            this._userPlaceMediaItems = (
                await getUserMedia(this._kernel.dataStore, this._userId)
            ).filter((x) => x.media.contributorId === this._userId);
        }
    }

    override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        this.addEventListener("ds-media-click", (e: CustomEvent) =>
            this._openMediaInGallery(e.detail.media)
        );
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
    }

    onChangeSet(_provider: DataProvider, _changeSet: ChangeSet<any>): void {}

    private _openMediaInGallery(media: IMedia) {
        if (this._userPlaceMediaItems) {
            const index = this._userPlaceMediaItems.findIndex((x) => x.media.id === media.id);
            const placeId = this._userPlaceMediaItems[index].placeMediaId;

            this._raiseEvent("ds-open-gallery", {
                placeId: placeId,
                filter: GEO_MEDIA_TYPES.includes(getMediaTypeFromMimeType(media.mimeType))
                    ? MEDIA_GALLERY_SURVEY_TYPES
                    : MEDIA_GALLERY_VIEW_TYPES,
                index: index,
                media: this._userPlaceMediaItems,
            });
        }
    }

    override render() {
        return html` <div class="body flex-row">
            <section class="left flex-column">
                <div class="info-box ds-box-shadow">
                    <h2>About</h2>
                    <ds-user-profile-about .userInfo=${this.userInfo}></ds-user-profile-about>
                </div>

                <div class="info-box ds-box-shadow">
                    <div class="info-box-header">
                        <h2>Gallery</h2>
                        <a @click=${() => this._raiseEvent("ds-show-view", { view: "Gallery" })}
                            >See All Media</a
                        >
                    </div>
                    <ds-media-thumbnail-grid
                        .media=${this._userPlaceMediaItems?.map((x) => x.media) ?? []}
                    ></ds-media-thumbnail-grid>
                </div>
            </section>

            <section class="right">
                <ds-user-profile-timeline .userInfo=${this.userInfo}></ds-user-profile-timeline>
            </section>
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-user-profile-posts": UserProfilePosts;
    }
}
